import { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import "../formFields/css/FormControlTokens.css";
import "./InputFields.css";

const PasswordInput = ({
  name,
  label,
  value,
  onChange,
  required = false,
  placeholder = "",
  requiredMessage = null,
  className = "",
  error: externalError = "",
  validationRules = [],
  disabled = false,
  shouldValidate = false,
}) => {
  const [internalError, setInternalError] = useState("");
  const [hasBeenTouched, setHasBeenTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Use external error if provided, otherwise use internal error
  const displayError = externalError || internalError;

  const validateField = useCallback(
    (inputValue) => {
      const trimmedValue = inputValue ? inputValue.trim() : "";

      if (required && !trimmedValue) {
        return requiredMessage || `Please enter ${label ? label.toLowerCase() : "a value"}`;
      }

      if (!trimmedValue && !required) {
        return "";
      }

      // if (trimmedValue.length < 8) {
      //   return "Password must be at least 8 characters long";
      // }

      // if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(trimmedValue)) {
      //   return "Password must contain at least one uppercase letter, one lowercase letter, and one number";
      // }

      for (const rule of validationRules) {
        const ruleError = rule(trimmedValue);
        if (ruleError) {
          return ruleError;
        }
      }

      return "";
    },
    [required, label, requiredMessage, validationRules],
  );

  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }

    // Only show validation after field has been touched and no external error
    if (hasBeenTouched && !externalError) {
      const validationError = validateField(e.target.value);
      setInternalError(validationError);
    }
  };

  const handleBlur = () => {
    setHasBeenTouched(true);
    if (!externalError) {
      const validationError = validateField(value);
      setInternalError(validationError);
    }
  };

  // Clear internal error when external error is provided
  useEffect(() => {
    if (externalError) {
      setInternalError("");
    }
  }, [externalError]);

  // Re-validate when value changes after field has been touched
  useEffect(() => {
    if (hasBeenTouched && !externalError) {
      const timeoutId = setTimeout(() => {
        const validationError = validateField(value);
        setInternalError(validationError);
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [value, hasBeenTouched, externalError, validateField]);

  // Trigger validation when shouldValidate prop changes
  useEffect(() => {
    if (shouldValidate && !externalError) {
      setHasBeenTouched(true);
      const validationError = validateField(value);
      setInternalError(validationError);
    }
  }, [shouldValidate, value, externalError, validateField]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="row mb-2">
      <div className="col-4 text-end d-flex align-items-center justify-content-end">
        <label htmlFor={name} className="form-label">
          {label}{" "}
          {required && <span className="required-asterisk text-danger">*</span>}
        </label>
      </div>
      <div className={label ? "col-8" : "col-12"}>
        <div className="input-group">
          <input
            type={showPassword ? "text" : "password"}
            id={name}
            name={name}
            value={value || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder || `Enter ${label ? label.toLowerCase() : "value"}...`}
            autoComplete="off"
            className={`form-control text-input-field ${displayError ? "is-invalid input-error" : ""} ${className}`}
          />
          <button
            className={`btn btn-outline-secondary ${displayError ? "password-toggle-btn-error" : ""}`}
            type="button"
            disabled={disabled}
            onClick={togglePasswordVisibility}
          >
            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
          </button>
        </div>
        {displayError && (
          <div className="error-message text-danger mt-1" style={{ fontSize: "0.875rem" }}>
            {displayError}
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordInput;